import Vue from 'vue'
import axios from 'axios'
import api from './api'
import qs from 'qs'
import { isTokenExpired } from '@/common/auth.js'
import utilsObj from '@/common/utils.js'
import {
  waitMpAuthReady,
  isSilentLoginPending,
  isMpGuestSession,
  isInMiniProgram,
  clearH5LoginStorage,
  applyRefreshTokenData,
  MP_CLIENT_KEY,
  beginMpAuthSync,
} from '@/common/mp-auth.js'
import { isEmbeddedIframe } from '@/embed-base'
import { isAuthTraceRequest, reportAuthTrace } from '@/common/auth-trace.js'
import { getError404Url } from '@/embed-base'

const utils = utilsObj.utils

beginMpAuthSync()

const AUTH_ERRCODES = new Set([101, 102, 103, 104, 105, 106, 401])

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL

axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8'
axios.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8'

axios.defaults.timeout = 200000

let refreshPromise = null

const isAuthError = (data = {}) => {
  const errcode = Number(data.errcode)
  const message = String(data.errmsg || data.message || '').trim()
  return AUTH_ERRCODES.has(errcode) || message === 'INVALID_SESSION'
}

const refreshH5Token = () => {
  if (refreshPromise) return refreshPromise
  const deviceId = localStorage.getItem('device_id') || ''
  if (!deviceId) return Promise.resolve(false)

  reportAuthTrace({
    event: 'REFRESH_START',
    api_path: api.postWxMiniH5RefreshToken,
    token_source: 'storage',
  })

  refreshPromise = axios
    .post(
      api.postWxMiniH5RefreshToken,
      {
        client_key: MP_CLIENT_KEY,
        device_id: deviceId,
      },
      {
        withCredentials: true,
        skipAuthRedirect: true,
        skipMpAuthWait: true,
      }
    )
    .then((res) => {
      const body = res && res.data ? res.data : res
      if (body && (body.errcode === 0 || body.errmsg === 'ok') && body.data && body.data.token) {
        reportAuthTrace({
          event: 'REFRESH_SUCCESS',
          token: body.data.token,
          token_source: 'login_response',
          api_path: api.postWxMiniH5RefreshToken,
        })
        return applyRefreshTokenData(body.data)
      }
      reportAuthTrace({
        event: 'REFRESH_FAILED',
        reason_code: 'REFRESH_FAILED',
        error_code: body && body.errcode,
        api_path: api.postWxMiniH5RefreshToken,
        token_source: 'storage',
      })
      return false
    })
    .catch(() => {
      reportAuthTrace({
        event: 'REFRESH_FAILED',
        reason_code: 'REFRESH_FAILED',
        api_path: api.postWxMiniH5RefreshToken,
        token_source: 'storage',
      })
      return false
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

const handleAuthFailure = (meta = {}) => {
  clearH5LoginStorage({
    clear_caller: meta.clear_caller || 'axios.handleAuthFailure',
    reason_code: meta.reason_code || 'SESSION_INVALID',
    clear_reason: meta.clear_reason || 'SESSION_INVALID',
    error_code: meta.error_code,
    api_path: meta.api_path,
  })
  if (isSilentLoginPending() || isMpGuestSession()) return
  if (window.__MP_LOGIN_REDIRECTING__) return
  window.__MP_LOGIN_REDIRECTING__ = '1'
  if (isEmbeddedIframe()) {
    window.__MP_LOGIN_REDIRECTING__ = ''
    utils.miniProgramLogin()
    return
  }
  utils.checkMiniProgram((isMiniProgram) => {
    if (isMiniProgram) {
      utils.miniProgramLogin()
      return
    }
    window.__MP_LOGIN_REDIRECTING__ = ''
  })
}

axios.interceptors.request.use(
  async (config) => {
    if (!config.skipMpAuthWait) {
      await waitMpAuthReady()
    }
    if (isTokenExpired() && !(isInMiniProgram() && localStorage.getItem('device_id'))) {
      const hadToken = !!(localStorage.getItem('tokenLogin') || localStorage.getItem('token'))
      if (hadToken) {
        reportAuthTrace({
          event: 'CLEAR_SESSION',
          reason_code: 'TOKEN_EXPIRED',
          clear_caller: 'axios.request:isTokenExpired',
          clear_reason: 'TOKEN_EXPIRED',
          token_source: 'storage',
          api_path: config.url,
        })
      }
      localStorage.removeItem('token')
      localStorage.removeItem('tokenLogin')
      localStorage.removeItem('expires')
    }
    let token = localStorage.getItem('tokenLogin') || sessionStorage.getItem('tokenLogin') || ''
    let uid = localStorage.getItem('uid') || ''
    let timestamp = localStorage.getItem('timestamp') || ''
    if (token) {
      config.headers['token'] = token
    }
    if (uid) {
      config.headers['uid'] = uid
    }
    if (timestamp) {
      config.headers['timestamp'] = timestamp
    }
    if (config.method === 'get') {
      config.paramsSerializer = function (params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    }
    if (!isAuthTraceRequest(config.url)) {
      reportAuthTrace({
        event: 'TOKEN_READ_FOR_REQUEST',
        reason_code: 'REQUEST_INTERCEPTOR',
        token_source: 'storage',
        api_path: config.url,
        token,
      })
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  async (response) => {
    if (response.config && response.config.skipAuthRedirect) {
      if (response.data.meta != null) {
        return response.data
      }
      return response
    }
    if (response.data.errcode == 404 || response.data.errmsg == '数据不存在' || response.data.errmsg == '艺人ID错误') {
      location.href = getError404Url()
      return false
    }
    if (isAuthError(response.data)) {
      const apiPath = (response.config && response.config.url) || ''
      const errcode = response.data && response.data.errcode
      reportAuthTrace({
        event: 'AUTH_ERROR_RECEIVED',
        reason_code: 'AUTH_CODE_RECEIVED',
        error_code: errcode,
        api_path: apiPath,
        token_source: 'storage',
      })
      if (isInMiniProgram()) {
        reportAuthTrace({
          event: 'WEBVIEW_AUTH_ERROR',
          reason_code: 'AUTH_CODE_RECEIVED',
          error_code: errcode,
          api_path: apiPath,
          token_source: 'webview',
        })
      }
      if (isSilentLoginPending()) {
        return response
      }
      const canRefresh =
        isInMiniProgram() && !isMpGuestSession() && localStorage.getItem('device_id') && !response.config.__mpRefreshed
      if (canRefresh) {
        const refreshed = await refreshH5Token()
        if (refreshed) {
          const retryConfig = { ...response.config, __mpRefreshed: true }
          retryConfig.headers = retryConfig.headers || {}
          retryConfig.headers.token = localStorage.getItem('tokenLogin') || ''
          return axios(retryConfig)
        }
      }
      handleAuthFailure({
        error_code: errcode,
        api_path: apiPath,
        reason_code: 'SESSION_INVALID',
        clear_reason: 'SESSION_INVALID',
      })
    }
    if (response.data.meta != null) {
      return response.data
    }
    return response
  },
  (error) => {
    return Promise.reject(error)
  }
)

const postData = (url, data, config = {}) => axios.post(api[url], data, config)
const getData = (url, params, config = {}) => axios.get(api[url], { ...config, params })
const putData = (url, data, config = {}) => axios.put(api[url], data, config)
const delData = (url, params, config = {}) => axios.delete(api[url], { params, ...config })

const postUrl = (url, path, data, config = {}) => axios.post(api[url] + path, data, config)
const getUrl = (url, path, params) => axios.get(api[url] + path, { params })
const putUrl = (url, path, data, config = {}) => axios.put(api[url] + path, data, config)
const delUrl = (url, path, params, config = {}) => axios.delete(api[url] + path, { params, ...config })
const patchUrl = (url, path, data, config = {}) => axios.patch(api[url] + path, data, config)

Vue.prototype.$postData = postData
Vue.prototype.$getData = getData
Vue.prototype.$putData = putData
Vue.prototype.$delData = delData

Vue.prototype.$postUrl = postUrl
Vue.prototype.$getUrl = getUrl
Vue.prototype.$putUrl = putUrl
Vue.prototype.$delUrl = delUrl
Vue.prototype.$patchUrl = patchUrl

Vue.prototype.$axios = axios

export default {
  postData,
  getData,
  putData,
  delData,
  postUrl,
  getUrl,
  putUrl,
  delUrl,
  patchUrl,
  axios,
}
